import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Word } from '../../types';
import { api, ApiError } from '../../services/api';
import ApproveButton from '../ApproveButton';
import ContentCard from '../ContentCard';
import CompactToggle from '../CompactToggle';
import { useUiStore } from '../../stores/uiStore';
import './StageCommon.css';
import './RelatedStage.css';

/**
 * Stage 8: Related Words — full inline editing.
 *
 * Stage 8 generation (per Path C) writes 3 full-content records to the Related
 * Words Content Airtable table. Rather than deferring review to Airtable, this
 * component now fetches those records from the backend and lets the editor
 * inline-edit every content field + regenerate any field, at parity with the
 * other review stages.
 *
 *   GET  /api/related?parentId=<word.id>   -> { parentWord, records, total }
 *   PUT  /api/related/:recordId {fields}   -> save one edited field
 *   POST /api/related/:recordId/regen      -> regenerate one field (writes through)
 *
 * The parent record (Stage 1) still supplies the 3 related-word names, shown as
 * a summary. The "Generate all 3" + Approve flow is unchanged. A "View in
 * Airtable" link remains as a fallback.
 */

const AIRTABLE_BASE_ID = 'appknlVfUqfAqvYK7';
const RELATED_WORDS_TABLE_ID = 'tblBKpVj4ac9biP1y';

interface RelatedRecord {
  id: string;
  word: string;
  action: string;
}

// One related-word content record fetched from the backend.
interface FetchedRelated {
  id: string;
  fields: Record<string, unknown>;
}

interface RelatedListResponse {
  parentWord: string;
  records: FetchedRelated[];
  total: number;
}

interface RegenResponse {
  id: string;
  field: string;
  value: string;
  model: string | null;
}

interface Stage8Failure {
  word: string;
  phase: string;
  message: string;
}

interface RelatedStageProps {
  word: Word;
  onGenerate: () => void;
  onApprove: (selections: Record<string, unknown>) => void;
  generating: boolean;
  saving: boolean;
  registerApprovePayload: (fn: (() => Record<string, unknown> | null) | null) => void;
  stage8Records: RelatedRecord[] | null;
  stage8Failures?: Stage8Failure[] | null;
  onClearFailures?: () => void;
}

// A related record is INCOMPLETE when core generated content is missing —
// e.g. a seed skeleton (definition only) or a word whose batch call failed
// mid-run. Without this check a half-generated record looks done.
function isIncompleteRecord(rec: FetchedRelated): boolean {
  const has = (f: string) => {
    const v = rec.fields[f];
    return v !== undefined && v !== null && String(v).trim() !== '';
  };
  return !has('definition_w1') || !has('etymology') || !has('example_sentence_1') || !has('fun_fact_1');
}

interface SourceRelatedWord {
  slot: 1 | 2 | 3;
  name: string;
  def: string;
  relationship: string;
}

interface FieldGroupDef {
  title: string;
  fields: string[];
}

// Editable content groups for each related-word record. Identity fields
// (word / source_game_word / relationship_type) are intentionally omitted —
// the backend rejects writes to them.
export const RELATED_FIELD_GROUPS: FieldGroupDef[] = [
  {
    title: 'Definition',
    fields: [
      'part_of_speech', 'phonetics', 'pronunciation_tip',
      'definition', 'definition_enhanced',
      'definition_w1', 'definition_w2', 'definition_w3',
      'etymology', 'usage_context',
    ],
  },
  {
    title: 'Examples',
    fields: ['example_sentence_1', 'example_sentence_2', 'example_sentence_3'],
  },
  {
    title: 'How To Use',
    fields: ['how_to_use_1', 'how_to_use_2', 'how_to_use_3'],
  },
  {
    title: 'Fun Facts',
    fields: ['fun_fact_1', 'fun_fact_2', 'fun_fact_3'],
  },
  {
    title: 'Theme & Vibe',
    fields: ['word_theme', 'word_vibe'],
  },
  {
    title: 'Marketing Signals',
    fields: ['problem_category', 'opposite_concept', 'psychological_triggers', 'sensitivity_flag'],
  },
];

export default function RelatedStage({
  word,
  onGenerate,
  onApprove,
  generating,
  saving,
  registerApprovePayload,
  stage8Records,
  stage8Failures,
  onClearFailures,
}: RelatedStageProps) {
  const sourceWords = useMemo<SourceRelatedWord[]>(() => (
    ([1, 2, 3] as const).map((slot) => ({
      slot,
      name: String(word[`related_word_${slot}`] ?? '').trim(),
      def: String(word[`related_word_${slot}_def`] ?? '').trim(),
      relationship: String(word[`related_word_${slot}_relationship`] ?? '').trim(),
    }))
  ), [word]);

  const hasAllNames = sourceWords.every((it) => it.name);
  const alreadyGenerated = Boolean(word.related_words_generated);
  const approved = Boolean(word.related_words_approved);

  const {
    records,
    loading,
    error,
    activeTab,
    setActiveTab,
    savingField,
    regeneratingField,
    fieldError,
    handleEdit,
    handleRegenerate,
    reload,
  } = useRelatedRecords(word, stage8Records);

  // One-level regen undo (session-only), two granularities:
  //  - per FIELD: value before the last per-field regen, keyed recId:field.
  //    Restore goes through handleEdit (the same PUT write-through a related
  //    edit/regen uses).
  //  - per RECORD: full editable-field snapshot before a whole-word regen.
  //    Restore PUTs the snapshot back and reloads.
  const [fieldUndo, setFieldUndo] = useState<Record<string, string>>({});
  const [recordUndo, setRecordUndo] = useState<Record<string, Record<string, unknown>>>({});
  const [undoingRecordId, setUndoingRecordId] = useState<string | null>(null);
  useEffect(() => { setFieldUndo({}); setRecordUndo({}); }, [word.id]);

  const EDITABLE_SNAPSHOT_FIELDS = useMemo(
    () => RELATED_FIELD_GROUPS.flatMap((g) => g.fields),
    []
  );

  const handleRegenerateFieldWithUndo = useCallback(async (recordId: string, field: string, instructions?: string) => {
    const rec = records.find((r) => r.id === recordId);
    const prev = rec ? toText(rec.fields[field]) : '';
    await handleRegenerate(recordId, field, instructions);
    if (prev.trim()) setFieldUndo((u) => ({ ...u, [`${recordId}:${field}`]: prev }));
  }, [records, handleRegenerate]);

  const handleUndoField = useCallback((recordId: string, field: string) => {
    const key = `${recordId}:${field}`;
    const prev = fieldUndo[key];
    if (prev === undefined) return;
    void handleEdit(recordId, field, prev);
    setFieldUndo((u) => {
      const next = { ...u };
      delete next[key];
      return next;
    });
  }, [fieldUndo, handleEdit]);

  // Per-word full regenerate (2026-07-05): POST /api/related/:id/regen-record
  // rebuilds ONE related word (same prompts + blind fakes as the batch)
  // without paying for the other two.
  const [regeneratingRecordId, setRegeneratingRecordId] = useState<string | null>(null);
  const [regenRecordError, setRegenRecordError] = useState<string | null>(null);
  const handleRegenerateRecord = useCallback(async (recordId: string) => {
    if (regeneratingRecordId || undoingRecordId) return;
    // Snapshot the editable fields for Go Back — only when the record has
    // real content (a seed skeleton has nothing worth restoring).
    const rec = records.find((r) => r.id === recordId);
    const snapshot: Record<string, unknown> = {};
    if (rec) {
      for (const f of EDITABLE_SNAPSHOT_FIELDS) {
        if (hasValue(rec.fields[f])) snapshot[f] = rec.fields[f];
      }
    }
    setRegeneratingRecordId(recordId);
    setRegenRecordError(null);
    try {
      await api.post(`/api/related/${recordId}/regen-record`, {});
      if (Object.keys(snapshot).length >= 3) {
        setRecordUndo((u) => ({ ...u, [recordId]: snapshot }));
      }
      await reload();
      onClearFailures?.();
    } catch (err) {
      setRegenRecordError((err as Error).message);
    } finally {
      setRegeneratingRecordId(null);
    }
  }, [regeneratingRecordId, undoingRecordId, records, EDITABLE_SNAPSHOT_FIELDS, reload, onClearFailures]);

  const handleUndoRecord = useCallback(async (recordId: string) => {
    const snapshot = recordUndo[recordId];
    if (!snapshot || undoingRecordId || regeneratingRecordId) return;
    setUndoingRecordId(recordId);
    setRegenRecordError(null);
    try {
      await api.put(`/api/related/${recordId}`, { fields: snapshot });
      setRecordUndo((u) => {
        const next = { ...u };
        delete next[recordId];
        return next;
      });
      await reload();
    } catch (err) {
      setRegenRecordError((err as Error).message);
    } finally {
      setUndoingRecordId(null);
    }
  }, [recordUndo, undoingRecordId, regeneratingRecordId, reload]);

  // Match a named batch failure to its (skeleton) record so the banner can
  // offer a one-click single-word retry.
  const recordIdForWord = useCallback((name: string): string | null => {
    const lower = name.trim().toLowerCase();
    return records.find((r) => String(r.fields.word || '').trim().toLowerCase() === lower)?.id || null;
  }, [records]);

  // "View in Airtable" — deep-link a specific Related Words Content record for
  // THIS parent. Airtable's web URL scheme reliably supports record links
  // (/baseId/tableId/recordId → opens the expanded record); it has NO working
  // URL parameter for a pre-filtered grid (the old ?filter_formula= was the
  // API's parameter and the web UI ignores it, which is why the button used to
  // land on the unfiltered table). Preference order: the record on the active
  // tab, else the first fetched record, else the first freshly-generated one.
  // No records yet → no link (handled at the render site).
  const airtableRecordId = records[activeTab]?.id ?? records[0]?.id ?? stage8Records?.[0]?.id ?? null;
  const airtableHref = airtableRecordId ? buildAirtableRecordLink(airtableRecordId) : null;

  const compactCards = useUiStore((s) => s.compactCards);

  // Approving also flips related_words_generated: after a partial batch run
  // repaired via single-word regens, the parent gate never flipped even though
  // all 3 records are complete — approval is the human's confirmation that
  // the set is done, so it closes both gates (2026-07-05).
  const handleApprove = useCallback(() => {
    onApprove({ related_words_approved: true, related_words_generated: true });
  }, [onApprove]);

  // Cmd+Enter approve must mirror the button's disabled condition — otherwise
  // the keyboard shortcut flips the parent gates with zero / incomplete /
  // already-approved records while the visible button is correctly disabled
  // (2026-07-05 fix). Return null in those states so the shortcut no-ops.
  useEffect(() => {
    if (!registerApprovePayload) return;
    registerApprovePayload(() => {
      if (approved) return null;
      if (records.length === 0) return null;
      if (records.some(isIncompleteRecord)) return null;
      return { related_words_approved: true, related_words_generated: true };
    });
    return () => { registerApprovePayload(null); };
  }, [registerApprovePayload, approved, records]);

  const currentRecord = records[activeTab];

  return (
    <div className="stage-view">
      <div className="related-summary">
        <h3 className="related-summary-title">Related words</h3>
        <p className="related-summary-hint">
          From Stage 1. Each one gets its own full content set. Edit any field below — changes save automatically.
        </p>

        {!hasAllNames && (
          <div className="related-stage1-missing">
            Stage 1 has not produced all 3 related words yet. Run Stage 1 first, then come back.
          </div>
        )}

        {hasAllNames && !alreadyGenerated && records.length === 0 && (
          <ol className="related-name-list">
            {sourceWords.map((it) => (
              <li key={it.slot} className="related-name-item">
                <div className="related-name-row">
                  <span className="related-name">{it.name}</span>
                  {it.relationship && (
                    <span className="related-relation-pill">{it.relationship}</span>
                  )}
                </div>
                {it.def && <p className="related-short-def">{it.def}</p>}
              </li>
            ))}
          </ol>
        )}
      </div>

      {hasAllNames && (
        <div className="related-action-bar">
          <button
            className="generate-btn"
            onClick={onGenerate}
            disabled={generating}
          >
            {generating
              ? (alreadyGenerated ? 'Regenerating all 3...' : 'Generating all 3...')
              : (alreadyGenerated ? 'Regenerate all 3' : 'Generate all 3')}
          </button>
          {airtableHref && (
            <a
              className="related-airtable-link"
              href={airtableHref}
              target="_blank"
              rel="noopener noreferrer"
              title="Open this word's related-word record in Airtable"
            >
              View in Airtable ↗
            </a>
          )}
        </div>
      )}

      {stage8Failures && stage8Failures.length > 0 && (
        <div className="related-partial-failures">
          <strong>
            {stage8Failures.length === 1
              ? `1 related word failed to generate — the other ${3 - stage8Failures.length} succeeded and are saved.`
              : `${stage8Failures.length} related words failed to generate.`}
          </strong>
          <ul>
            {stage8Failures.map((f) => {
              const recId = recordIdForWord(f.word);
              return (
                <li key={f.word}>
                  <span className="failure-word">{f.word}</span>
                  <span className="failure-detail"> [{f.phase}] {f.message}</span>
                  {recId && (
                    <button
                      className="related-retry-btn"
                      onClick={() => void handleRegenerateRecord(recId)}
                      disabled={regeneratingRecordId !== null || generating}
                    >
                      {regeneratingRecordId === recId ? 'Regenerating…' : `Retry ${f.word} only`}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          {onClearFailures && (
            <button className="related-retry-btn" onClick={onClearFailures}>Dismiss</button>
          )}
        </div>
      )}

      {regenRecordError && (
        <div className="related-fetch-error">
          <span>{regenRecordError}</span>
          <button type="button" className="related-retry-btn" onClick={() => setRegenRecordError(null)}>Dismiss</button>
        </div>
      )}

      {loading && (
        <div className="related-loading">Loading related word content…</div>
      )}

      {error && !loading && (
        <div className="related-fetch-error">
          <span>{error}</span>
          <button type="button" className="related-retry-btn" onClick={reload}>Retry</button>
        </div>
      )}

      {!loading && !error && records.length > 0 && (
        <>
          <div className="related-tabs">
            {records.map((rec, idx) => (
              <button
                key={rec.id}
                className={`related-tab ${activeTab === idx ? 'tab-active' : ''}`}
                onClick={() => setActiveTab(idx)}
              >
                <span className="tab-word">{String(rec.fields.word || `Related ${idx + 1}`)}</span>
                {rec.fields.relationship_type ? (
                  <span className="tab-relation">{String(rec.fields.relationship_type)}</span>
                ) : null}
                {isIncompleteRecord(rec) && (
                  <span className="tab-incomplete" title="Missing generated content (fakes, etymology, examples, or facts) — regenerate this word">
                    incomplete
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="stage-toolbar">
            <button className="regenerate-btn" onClick={onGenerate} disabled={generating || regeneratingRecordId !== null} title="Regenerates ALL THREE of this word's related words (3× cost)">
              {generating ? 'Regenerating all 3...' : 'Regenerate all 3'}
            </button>
            {currentRecord && (
              <button
                className="regenerate-btn related-regen-one"
                onClick={() => void handleRegenerateRecord(currentRecord.id)}
                disabled={generating || regeneratingRecordId !== null || undoingRecordId !== null || approved}
                title={`Regenerate only "${String(currentRecord.fields.word || 'this word')}" — the other two are untouched`}
              >
                {regeneratingRecordId === currentRecord.id
                  ? `Regenerating ${String(currentRecord.fields.word || '')}…`
                  : `Regenerate ${String(currentRecord.fields.word || 'this word')} only`}
              </button>
            )}
            {currentRecord && recordUndo[currentRecord.id] && !approved && (
              <button
                className="regenerate-btn related-undo-record"
                onClick={() => void handleUndoRecord(currentRecord.id)}
                disabled={generating || regeneratingRecordId !== null || undoingRecordId !== null}
                title={`Restore "${String(currentRecord.fields.word || 'this word')}" to its content from before the last full regeneration`}
              >
                {undoingRecordId === currentRecord.id ? 'Restoring…' : '↶ Go Back'}
              </button>
            )}
            <CompactToggle />
          </div>

          {regeneratingRecordId && (
            <div className="stage-loading-bar" role="status" aria-live="polite">
              <div className="stage-loading-spinner" aria-hidden="true" />
              <span>
                Regenerating {String(records.find((r) => r.id === regeneratingRecordId)?.fields.word || 'related word')}… (full content + fakes, ~30–60s)
              </span>
            </div>
          )}
          {undoingRecordId && (
            <div className="stage-loading-bar" role="status" aria-live="polite">
              <div className="stage-loading-spinner" aria-hidden="true" />
              <span>
                Restoring {String(records.find((r) => r.id === undoingRecordId)?.fields.word || 'related word')} to its previous version…
              </span>
            </div>
          )}

          {fieldError && (
            <div className="related-field-error">{fieldError}</div>
          )}

          {currentRecord && (
            <div className="review-groups">
              {RELATED_FIELD_GROUPS.map((group) => {
                const filled = group.fields.filter((f) => hasValue(currentRecord.fields[f]));
                if (filled.length === 0) return null;
                return (
                  <div key={group.title} className="review-group">
                    <h3 className="review-group-title">{group.title}</h3>
                    <div className="review-group-cards">
                      {filled.map((field) => (
                        <ContentCard
                          key={field}
                          id={field}
                          content={toText(currentRecord.fields[field])}
                          label={formatFieldLabel(field)}
                          showCheckbox={false}
                          compact={compactCards}
                          disabled={approved}
                          onEdit={(fid, val) => handleEdit(currentRecord.id, fid, val)}
                          onRegenerate={approved ? undefined : (fid, instr) => handleRegenerateFieldWithUndo(currentRecord.id, fid, instr)}
                          onUndo={!approved && fieldUndo[`${currentRecord.id}:${field}`] !== undefined ? () => handleUndoField(currentRecord.id, field) : undefined}
                          regenerating={regeneratingField === `${currentRecord.id}:${field}`}
                          emptyPlaceholder="Not generated yet"
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
              {(savingField || regeneratingField) && (
                <div className="related-saving-indicator" aria-live="polite">
                  {regeneratingField ? 'Regenerating…' : 'Saving…'}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Approve is available whenever the 3 records exist — NOT gated on the
          parent's related_words_generated flag, which stays down after a
          partial batch run even once single-word regens finish the set
          (2026-07-05 fix: the button never appeared in that state). Incomplete
          records block approval with an explanation instead of hiding it. */}
      {hasAllNames && !approved && (alreadyGenerated || records.length > 0) && (
        <div className="related-approve-area">
          {records.some(isIncompleteRecord) && (
            <p className="related-approve-hint">
              {records.filter(isIncompleteRecord).map((r) => String(r.fields.word || '?')).join(', ')} still missing generated content — regenerate before approving.
            </p>
          )}
          <ApproveButton
            onClick={handleApprove}
            disabled={saving || records.length === 0 || records.some(isIncompleteRecord)}
            loading={saving}
            label="Approve & Advance to Final Review"
          />
        </div>
      )}

      {approved && (
        <div className="related-approved-badge">
          ✓ Related words approved
        </div>
      )}
    </div>
  );
}

/**
 * Shared hook: fetch the related-word content records for a parent word and
 * manage inline edit (PUT) + per-field regen (POST) against them. Reused by
 * both Stage 8 and Stage 9.
 *
 * `stage8Records` is only used as a trigger to re-fetch after a fresh
 * generation run (the parent surfaces it when generation completes).
 */
export function useRelatedRecords(word: Word, refreshTrigger?: unknown) {
  const [records, setRecords] = useState<FetchedRelated[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [regeneratingField, setRegeneratingField] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const inFlightRef = useRef(false);
  const parentId = word.id;
  const parentWord = String(word.word ?? '');

  const fetchRecords = useCallback(async () => {
    if (!parentId && !parentWord) return;
    setLoading(true);
    setError(null);
    try {
      const query = parentId
        ? `parentId=${encodeURIComponent(parentId)}`
        : `word=${encodeURIComponent(parentWord)}`;
      const res = (await api.get(`/api/related?${query}`)) as RelatedListResponse;
      setRecords(Array.isArray(res?.records) ? res.records : []);
      setActiveTab((prev) => (prev < (res?.records?.length ?? 0) ? prev : 0));
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't load related word content.";
      setError(msg);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [parentId, parentWord]);

  // Fetch on mount / when parent changes / when a fresh generation lands.
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords, refreshTrigger]);

  const applyLocal = useCallback((recordId: string, field: string, value: unknown) => {
    setRecords((prev) => prev.map((r) => (
      r.id === recordId ? { ...r, fields: { ...r.fields, [field]: value } } : r
    )));
  }, []);

  const handleEdit = useCallback(async (recordId: string, field: string, newValue: string) => {
    const rec = records.find((r) => r.id === recordId);
    if (rec && toText(rec.fields[field]) === newValue) return; // no change
    applyLocal(recordId, field, newValue);
    setFieldError(null);
    setSavingField(`${recordId}:${field}`);
    try {
      await api.put(`/api/related/${recordId}`, { fields: { [field]: newValue } });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "That edit didn't save. Try again.";
      setFieldError(msg);
    } finally {
      setSavingField(null);
    }
  }, [records, applyLocal]);

  const handleRegenerate = useCallback(async (recordId: string, field: string, instructions?: string) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setFieldError(null);
    setRegeneratingField(`${recordId}:${field}`);
    try {
      const res = (await api.post(`/api/related/${recordId}/regen`, { field, instructions })) as RegenResponse;
      if (res && typeof res.value === 'string') {
        applyLocal(recordId, field, res.value);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Regeneration didn't work. Try again.";
      setFieldError(msg);
    } finally {
      setRegeneratingField(null);
      inFlightRef.current = false;
    }
  }, [applyLocal]);

  return {
    records,
    loading,
    error,
    activeTab,
    setActiveTab,
    savingField,
    regeneratingField,
    fieldError,
    handleEdit,
    handleRegenerate,
    reload: fetchRecords,
  };
}

export function hasValue(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v === 'boolean') return true;
  return String(v).length > 0;
}

export function toText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

export function formatFieldLabel(fieldName: string): string {
  // definition_w1/2/3 hold the wrong answers for the related word's game —
  // "Fake Definition N" says what they are; "Definition Word N" didn't.
  // Display-only: the Airtable columns keep their definition_wN names.
  const fake = fieldName.match(/^definition_w([123])$/i);
  if (fake) return `Fake Definition ${fake[1]}`;
  return fieldName
    .replace(/_/g, ' ')
    .replace(/\bw1\b/i, 'Word 1')
    .replace(/\bw2\b/i, 'Word 2')
    .replace(/\bw3\b/i, 'Word 3')
    .replace(/\bpaa\b/i, 'PAA')
    .replace(/\bcta\b/i, 'CTA')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Deep link to one Related Words Content record. This is the most specific
 * target Airtable's web URLs reliably support: /baseId/tableId/recordId opens
 * the table with that record's card expanded. Per-parent FILTERED views via
 * URL are not supported by the web UI (the previous ?filter_formula= param is
 * API-only and was silently ignored — the button landed on the raw table).
 */
function buildAirtableRecordLink(recordId: string): string {
  return `https://airtable.com/${AIRTABLE_BASE_ID}/${RELATED_WORDS_TABLE_ID}/${recordId}`;
}
